import { hasTranslation, t, getLocale } from '@/i18n';
import { shouldHideKoreanContent } from '@/shared/lib/i18n/korean-only-content';
import { formatLocalizedDate } from '@/shared/lib/utils/format';

import type {
  FinancialCalendarDailyCount,
  FinancialCalendarEvent,
  FinancialEventImpact,
} from '@/entities/financial-calendar';

import type { CalendarCountry, CalendarFilter } from './types';
import type { FinancialEventCategory } from '@/entities/financial-calendar';
import { defineServerEnum, unknownLabel } from '@/shared/lib/utils/server-enum';

// ─── Date helpers ────────────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');

export const formatDateParam = (date: Date): string => {
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  return `${y}-${m}-${d}`;
};

/** Returns the Monday of the week containing the given date (ISO week: Mon–Sun). */
export const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Returns the Sunday of the week containing the given date. */
export const getWeekEnd = (date: Date): Date => {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return end;
};

/** Returns [from, to] strings for the current week (Mon–Sun). */
export const getCurrentWeekRange = (): { from: string; to: string } => {
  const today = new Date();
  return {
    from: formatDateParam(getWeekStart(today)),
    to: formatDateParam(getWeekEnd(today)),
  };
};

/**
 * Returns the adjusted from/to range when switching views.
 * - week: Mon–Sun of the week containing `from`
 * - month: 1st–last day of the month containing `from`
 * - list: null (no adjustment)
 */
export const getViewAdjustedRange = (
  from: string,
  view: 'list' | 'week' | 'month',
): { from: string; to: string } | null => {
  if (view === 'list') return null;
  const date = new Date(from + 'T00:00:00');
  if (view === 'week') {
    return {
      from: formatDateParam(getWeekStart(date)),
      to: formatDateParam(getWeekEnd(date)),
    };
  }
  // month
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return {
    from: formatDateParam(firstDay),
    to: formatDateParam(lastDay),
  };
};

// ─── URL ↔ Filter conversion ─────────────────────────────────────────────────

// TRAD-2141 — 🔴 CRYPTO 임시 숨김 (2026-07-31). CoinMarketCal API v1 폐기(7/14)로 수집이 멈췄고
//   운영 DB 기준 마지막 예정일이 2026-07-25, **미래 CRYPTO 이벤트 0건**이라 빈 화면만 보인다.
// 복구 조건: TRAD-2129(v2 전환)로 수집이 재개되고 **미래 이벤트 > 0** 이 확인되면 되돌린다.
// 복구 방법: 이 배열에 'CRYPTO' 재추가 + `ui/CalendarFilterBar.tsx`·`ui/CalendarFilterDrawer.tsx`
//   의 CATEGORIES 에 재추가 = 총 3줄. 라벨·배지색·월/주뷰 색·CoinMarketCal 표기는 **일부러 남겨뒀다**
//   (참조되지 않아 무해하고, 지우면 복구 비용만 오른다). 되돌릴 때 `__tests__/crypto-hidden.test.ts` 도 함께 삭제.
export const VALID_CATEGORIES: FinancialEventCategory[] = ['INDICATOR', 'EARNINGS', 'DISCLOSURE'];
// TRAD-2310 — `OTHER` 는 국가 코드가 아니라 서버 센티널이다(정의는 `types.ts` CalendarCountry 주석).
// 이 배열의 **길이**가 "전부 선택 = 파라미터 생략" 판정의 기준이라(`shouldSendCountryParam`),
// 여기에 값을 더하면 URL 직렬화·초기화·pristine 판정이 전부 따라온다.
export const VALID_COUNTRIES: CalendarCountry[] = ['KR', 'US', 'OTHER'];
export const VALID_IMPACTS: FinancialEventImpact[] = ['HIGH', 'MEDIUM', 'LOW'];

// TRAD-2742 — TRAD-2071 정책 반전(HIGH만 → HIGH+MEDIUM).
// 2071의 전제는 "HIGH가 충분히 있다"였는데 실측상 30일 9건(주 2.1건)뿐이었다. 등급화가
// 1단계에 멈춰 EARNINGS가 전부 LOW였기 때문. TRAD-2075로 카탈로그 승격(MEDIUM)이 붙으면서
// 기본 노출을 MEDIUM까지 넓힌다.
// 🔴 이 상수 변경만으로는 dev 화면이 안 바뀐다 — dev는 스케줄러가 없어(팀 룰) 수집이 한 번도
// 안 돌았고, 2026-08-01~08-31 기준 dev는 HIGH 8건 외엔 MEDIUM/LOW 전부 0건이다(운영은 같은
// 창에서 HIGH 8 / MEDIUM 203 / LOW 9,690 / 전체 9,901 — 환경 간 데이터량 차이가 매우 크다).
// 의미 있는 변화는 TRAD-2075 승격이 붙을 때 생긴다.
// 파라미터 없음(최초 진입) = 이 기본값. VALID_IMPACTS 전체가 아님에 주의.
export const DEFAULT_IMPACTS: FinancialEventImpact[] = ['HIGH', 'MEDIUM'];

/**
 * 두 중요도 집합이 순서 무관 동일한지 비교.
 * "주요만/전체" 세그먼트 토글의 active 상태 판정, 필터 초기화 여부 판정에 사용.
 */
export const isSameImpactSet = (impacts: FinancialEventImpact[], target: FinancialEventImpact[]): boolean =>
  impacts.length === target.length && target.every(v => impacts.includes(v));

/** 필터 축 — 셋 중 하나라도 비면 조회 자체를 하지 않는다(쿼리 disabled). */
export type FilterAxis = 'categories' | 'countries' | 'impacts';
const FILTER_AXES: readonly FilterAxis[] = ['categories', 'countries', 'impacts'];

/**
 * TRAD-2831 — 비어 있는 필터 축 목록(화면 표시 순서). 종전 `isAllDeselected` 는 OR 조건(한 축만
 * 비어도 참)인데 이름이 «전부 해제»라 문구("선택된 필터가 없습니다")도 같은 오해를 담았다.
 * 길이 0 = 조회 가능 / 1 = 그 축을 지목 / 2+ = 일반 문구.
 */
export const getEmptyFilterAxes = (filter: Record<FilterAxis, readonly unknown[]>): FilterAxis[] =>
  FILTER_AXES.filter(axis => filter[axis].length === 0);

/**
 * 단순 토글 헬퍼.
 * - 포함 → 제거 (비활성)
 * - 미포함 → 추가 (활성)
 * [] = 아무것도 선택 안 됨 = 빈 결과 (쿼리 disabled)
 */
export const toggleChip = <T>(current: T[], value: T): T[] =>
  current.includes(value) ? current.filter(v => v !== value) : [...current, value];

/**
 * 서버에 `country` 파라미터를 실어 보낼지 판정 (TRAD-2310).
 *
 * **전부 선택 = 파라미터 생략**이 규약이고, 그때 서버는 칩에 없는 국가까지 전부 돌려준다.
 * 🔴 이 함수를 `query-options`(무엇을 요청할지)와 필터바(무엇을 표시할지)가 **함께** 쓴다.
 * 두 곳이 각자 조건을 복사해 두면 한쪽만 고쳐졌을 때 "칩은 KR/US 뿐인데 목록엔 GB 가 있는"
 * 상태가 다시 생긴다 — 그게 이 티켓의 원 결함이었다.
 */
export const shouldSendCountryParam = (countries: CalendarCountry[]): boolean =>
  countries.length > 0 && countries.length < VALID_COUNTRIES.length;

/**
 * 국가 필터 칩의 정의 — **데스크톱 필터바와 모바일 드로어가 공유한다** (TRAD-2310).
 *
 * 이전에는 두 UI 가 각자 `COUNTRIES` 배열을 복사해 두어, 한쪽만 고치면 데스크톱과 모바일의
 * 칩 구성이 갈라졌다. 칩 ↔ 서버 파라미터 정합이 이 티켓의 원 결함이었던 만큼, 칩 목록 자체를
 * 한 곳에서만 정의한다.
 *
 * 🔴 **상수가 아니라 함수인 이유**: `OTHER` 라벨은 로케일에 따라 달라진다("기타"/"Other").
 * 모듈 로드 시점에 `t()` 를 굳혀 두면 언어를 바꿔도 라벨이 옛 로케일에 남는다. 호출 시점에
 * 평가하도록 함수로 둔다 — 로케일이 같으면 같은 값을 내는 순수 함수다.
 *
 * KR·US 는 국기 + ISO 코드라 번역 대상이 아니지만, `OTHER` 는 **낱말**이라 카탈로그를 거친다.
 */
export const getCountryChips = (): { value: CalendarCountry; label: string }[] => [
  { value: 'KR', label: '🇰🇷 KR' },
  { value: 'US', label: '🇺🇸 US' },
  { value: 'OTHER', label: `${GLOBAL_FLAG} ${t('financial_calendar.filter.country_other')}` },
];

export const parseCategories = (param: string | null): FinancialEventCategory[] => {
  if (!param) return [];
  return param
    .split(',')
    .map(s => s.trim().toUpperCase())
    .filter((s): s is FinancialEventCategory => VALID_CATEGORIES.includes(s as FinancialEventCategory));
};

export const parseCountries = (param: string | null): CalendarCountry[] => {
  if (!param) return [];
  return param
    .split(',')
    .map(s => s.trim().toUpperCase())
    .filter((s): s is CalendarCountry => VALID_COUNTRIES.includes(s as CalendarCountry));
};

export const parseImpacts = (param: string | null): FinancialEventImpact[] => {
  if (!param) return [];
  return param
    .split(',')
    .map(s => s.trim().toUpperCase())
    .filter((s): s is FinancialEventImpact => VALID_IMPACTS.includes(s as FinancialEventImpact));
};

// ─── Event grouping ───────────────────────────────────────────────────────────

/**
 * 중요도 정렬 가중치(낮을수록 우선순위 높음 = 먼저 표시).
 * null(코인 등 impact 미부여)은 항상 마지막.
 * FullCalendar `eventOrder` 문자열 필드 스펙(오름차순 비교)과 List 뷰 정렬 양쪽에서 재사용.
 */
export const getImpactWeight = (impact: FinancialEventImpact | null): number => {
  switch (impact) {
    case 'HIGH':
      return 0;
    case 'MEDIUM':
      return 1;
    case 'LOW':
      return 2;
    default:
      return 3;
  }
};

/**
 * TRAD-2071 §5.2 — 같은 날 안에서 중요도 desc → 시각 asc.
 * "주요만" 기본 노출 정책상 같은 날의 상위 이벤트가 먼저 눈에 띄어야 한다.
 */
export const compareByImportanceThenTime = (a: FinancialCalendarEvent, b: FinancialCalendarEvent): number => {
  const impactDiff = getImpactWeight(a.impact) - getImpactWeight(b.impact);
  if (impactDiff !== 0) return impactDiff;
  return a.scheduledAt.localeCompare(b.scheduledAt);
};

/** Groups events by date string (YYYY-MM-DD extracted from scheduledAt). */
export const groupEventsByDate = (
  events: FinancialCalendarEvent[],
): { date: string; events: FinancialCalendarEvent[] }[] => {
  const map = new Map<string, FinancialCalendarEvent[]>();
  for (const event of events) {
    const date = event.scheduledAt.slice(0, 10);
    const group = map.get(date) ?? [];
    group.push(event);
    map.set(date, group);
  }
  // Sort by date ascending
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, evts]) => ({ date, evts: [...evts].sort(compareByImportanceThenTime) }))
    .map(({ date, evts }) => ({ date, events: evts }));
};

/**
 * 특정 날짜의 {high,total} 집계를 찾는다 (TRAD-2071 "전체 M건" 고지용).
 * 백엔드 필드(dailyCounts)가 아직 없거나 해당 날짜 데이터가 없으면 undefined —
 * 호출부(DateGroupHeader)는 이 경우 고지 문구를 조용히 생략한다.
 */
export const findDailyCount = (
  dailyCounts: FinancialCalendarDailyCount[] | undefined,
  date: string,
): FinancialCalendarDailyCount | undefined => dailyCounts?.find(c => c.date === date);

/**
 * TRAD-2071 — 날짜 헤더 "전체 M건 보기" 고지 여부 판정(순수 함수).
 * 셋 다 충족해야 고지: ①현재 중요도 필터로 걸러진 상태 ②백엔드가 전체 건수를 내려줌(undefined 아님)
 * ③실제로 화면에 없는 이벤트가 있음(total > shown). 하나라도 아니면 조용히 생략.
 */
export const shouldShowDailyCountNotice = (
  isFiltered: boolean,
  shownCount: number,
  totalCount: number | undefined,
): boolean => isFiltered && totalCount !== undefined && totalCount > shownCount;

/**
 * TRAD-3628 — 날짜 헤더 «주요 n건»의 n = 그 날짜의 «선택 중요도» 서버 집계 합(순수 함수).
 * 종전엔 n 이 «지금까지 로드된 그 날짜 이벤트 수»였다. 리스트는 날짜 asc 오프셋 페이지(backend
 * FinancialEventQueryRepositoryImpl orderBy scheduledDate asc · pageSize 20)라 날짜가 페이지 경계에 걸리면
 * 다음 페이지 전까지 n 이 덜 셌다(운영 2026-09-03: 로드 11 · 서버 medium 19).
 * 🔴 필요한 필드가 하나라도 없으면(옛 서버 — medium/low 미제공) `undefined` — 호출부가 종전 값(로드 수)으로 폴백한다.
 * 없는 집계를 0 으로 메우면 «0건» 이라는 사실 주장이 된다.
 */
export const selectedImpactDailyCount = (
  count: FinancialCalendarDailyCount | undefined,
  impacts: readonly FinancialEventImpact[],
): number | undefined => {
  if (!count || impacts.length === 0) return undefined;
  const byImpact: Record<FinancialEventImpact, number | undefined> = {
    HIGH: count.highCount,
    MEDIUM: count.mediumCount,
    LOW: count.lowCount,
  };
  const parts = impacts.map(i => byImpact[i]);
  return parts.every((v): v is number => typeof v === 'number') ? parts.reduce((a, b) => a + b, 0) : undefined;
};

/**
 * 조회 기간 전체의 중요도-무관 이벤트 수 (TRAD-2334).
 *
 * `daily-counts` 는 목록과 **같은 기간·카테고리·국가**로 부르되 **importance 는 보내지 않는다**
 * (엔드포인트 시그니처가 그것을 보장한다 — `query-options` 주석 참조). 그래서 이 합계는
 * "중요도 필터만 풀면 몇 건이 보이는가"를 정확히 말해 준다.
 *
 * 🔴 `undefined` 를 `0` 으로 접지 않는다. 백엔드 미연동(undefined)과 "정말 0건"은 다른 사실이고,
 * `shouldShowDailyCountNotice` 가 그 구분에 기대어 미연동 시 고지를 **조용히 생략**한다.
 * 0 으로 접으면 미연동이 "0건"이라는 단언으로 바뀐다.
 */
export const sumDailyCountTotals = (dailyCounts: FinancialCalendarDailyCount[] | undefined): number | undefined =>
  dailyCounts === undefined ? undefined : dailyCounts.reduce((sum, c) => sum + c.totalCount, 0);

/**
 * TRAD-2831 — «필터가 적용 중인가»(빈 상태가 «필터 조건에 맞는 이벤트가 없습니다» vs «선택한 기간에…» 중 무엇을 말할지).
 * 전부 활성 = pristine(필터 없음). 하나라도 좁혀져 있으면 필터 적용 중.
 * TRAD-2071: impacts 의 pristine 기준은 DEFAULT_IMPACTS — VALID_IMPACTS 전체가 아니다.
 * 🔴 리스트(EventList)와 주간·월간(페이지)이 «같은 함수»를 쓴다 — 종전엔 EventList 안에만 있어 격자 뷰는 0건을 말하지 못했다.
 */
export const hasActiveCalendarFilter = (
  filter: Pick<CalendarFilter, 'categories' | 'countries' | 'impacts'>,
  /**
   * TRAD-3725 — 중요도의 «필터 없음» 기준. 메인 캘린더 = DEFAULT_IMPACTS(TRAD-2071).
   * 일자 페이지는 기본이 «전체 중요도»(calendarDayFilter)라 그 화면의 기준을 넘긴다 — 종전엔 일자 페이지 기본 필터를
   * «필터 적용 중»으로 오판해 0건 날짜에 «필터 조건에 맞는 이벤트가 없습니다» + 아무 일도 안 하는 «필터 초기화»를 보였다.
   */
  pristineImpacts: FinancialEventImpact[] = DEFAULT_IMPACTS,
): boolean =>
  filter.categories.length < VALID_CATEGORIES.length ||
  filter.countries.length < VALID_COUNTRIES.length ||
  !isSameImpactSet(filter.impacts, pristineImpacts);

/**
 * TRAD-2831 — 0건인데 «중요도만 넓히면 볼 게 있는가»(TRAD-2334)의 건수. 있으면 그 건수, 아니면 `undefined`.
 * 날짜 헤더 고지와 같은 판정(`shouldShowDailyCountNotice`)을 «기간 전체 · 보이는 0건»으로 묻는다.
 * 🔴 리스트·주간·월간 공용 — 한쪽만 「전체 M건 보기」를 주면 같은 상황에 다른 말을 한다.
 */
export const resolveExpandableTotal = (
  filter: Pick<CalendarFilter, 'impacts'>,
  dailyCounts: FinancialCalendarDailyCount[] | undefined,
): number | undefined => {
  const total = sumDailyCountTotals(dailyCounts);
  return shouldShowDailyCountNotice(!isSameImpactSet(filter.impacts, VALID_IMPACTS), 0, total) ? total : undefined;
};

// ─── Display helpers ──────────────────────────────────────────────────────────

/**
 * TRAD-2071 — 이벤트 제목 렌더링 규칙: titleKey가 있고 카탈로그에 실제로 존재하면 i18n으로 렌더,
 * 그 외(기존 이벤트=titleKey 없음, 또는 카탈로그에 아직 없는 키)에는 title(백엔드가 항상 함께 내려주는
 * 영문 폴백/기존 원문)로 조용히 폴백한다. 카탈로그 누락으로 raw 키 문자열이 노출되는 사고를 방지.
 */
export const getEventTitle = (
  event: Pick<FinancialCalendarEvent, 'title' | 'titleKey'>,
  locale: string = getLocale(),
): string => {
  if (event.titleKey && hasTranslation(event.titleKey)) {
    return t(event.titleKey);
  }
  // 2026-10-27 — davidyoon 10/17 결정: 한국어 화면이 아니면 한국어 제목(DART 공시·실적 — 언어 태그 없음, TRAD-2876/2914)을
  // 그대로 보이지 않고 «Coming soon». 운영 10/20 일자 페이지에서 EN 에 한국어 제목 11줄 실측(감사 docs-ops cf089fa).
  // 카드를 지우지 않고 제목만 바꾼다 — 일자별 건수(daily-counts)와 목록 건수가 어긋나지 않게. 모든 표시 지점(7곳)이 이 함수를 지난다.
  if (shouldHideKoreanContent(locale, event.title)) {
    return t('common.coming_soon');
  }
  return event.title;
};

export const formatScheduledTime = (scheduledAt: string): string => {
  const time = scheduledAt.slice(11, 16); // HH:mm
  if (time === '00:00') return t('financial_calendar.event.all_day');
  return time;
};

export const formatScheduledDate = (scheduledAt: string): string => scheduledAt.slice(0, 10);

// ─── TRAD-2897/2899 — 시각 정밀도 · 발표 상태 ──────────────────────────────────
//
// 정본: Confluence 282329090 §5-19(시각)·§5-20(배지), iOS 45ba3c6d(FinancialCalendarEventStatus)와 같은 규칙·같은 KO/EN.
// 🔴 «필드 없음»과 «미지값»은 다르다:
//   - 필드 없음(undefined·null — 옛 서버, 지금 운영) → **종전 로직 그대로**(시각: 00:00 → 종일 / 배지: confirmed).
//     이걸 «알 수 없음»으로 그리면 서버 배포 전까지 전 이벤트가 «알 수 없음»이 된다.
//   - 미지값(신서버의 모르는 값) → «알 수 없음 / Unknown». 🔴 아는 값으로 대체 금지 — scheduledAt 의 시:분을 대신 찍지
//     않는다(그게 «12:00» 결함이다: dev 500/9,004 표본에서 DATE_ONLY 인데 12:00 으로 찍힌 것 349건), 배지는 «발표완료»로
//     대체하지 않는다.
// 목록 카드·상세 패널·상세 드로어(3자리)가 **같은 함수**를 쓴다 — 종전엔 각자 slice(11,16) 를 구현해 갈릴 수 있었다.

const KNOWN_TIME_PRECISIONS = ['EXACT', 'BEFORE_OPEN', 'AFTER_CLOSE', 'DATE_ONLY'] as const;
const KNOWN_RELEASE_STATUSES = ['TENTATIVE', 'SCHEDULED', 'RELEASED'] as const;
export type TimePrecision = (typeof KNOWN_TIME_PRECISIONS)[number] | 'UNKNOWN';
export type ReleaseStatus = (typeof KNOWN_RELEASE_STATUSES)[number] | 'UNKNOWN';

/** null = 필드 없음(종전 로직), 'UNKNOWN' = 값은 왔는데 모르는 값. */
export const parseTimePrecision = (raw: string | null | undefined): TimePrecision | null =>
  raw == null ? null : ((KNOWN_TIME_PRECISIONS as readonly string[]).includes(raw) ? (raw as TimePrecision) : 'UNKNOWN');

export const parseReleaseStatus = (raw: string | null | undefined): ReleaseStatus | null =>
  raw == null ? null : ((KNOWN_RELEASE_STATUSES as readonly string[]).includes(raw) ? (raw as ReleaseStatus) : 'UNKNOWN');

/**
 * 시각 자리 문구. `withZone` = 상세(패널·드로어)처럼 실제 시각 뒤에 « KST» 를 붙이는 자리.
 * « KST» 는 실제 시각(EXACT · 필드 없음의 HH:mm)에만 붙는다 — «장 시작 전»·«시간 정보 없음» 같은 문구엔 안 붙인다.
 */
export const eventTimeLabel = (
  event: Pick<FinancialCalendarEvent, 'scheduledAt' | 'timePrecision'>,
  { withZone }: { withZone: boolean },
): string => {
  const clock = event.scheduledAt.slice(11, 16); // HH:mm
  const withKst = (hhmm: string) => (withZone ? `${hhmm} KST` : hhmm);
  switch (parseTimePrecision(event.timePrecision)) {
    case null: {
      // 필드 없음 — 종전 로직(TRAD-2720 «00:00 → 종일») 그대로.
      const legacy = formatScheduledTime(event.scheduledAt);
      return legacy === clock ? withKst(clock) : legacy;
    }
    case 'EXACT':
      // 실제 시각을 안다 — 00:00 도 진짜 자정이므로 «종일» 로 바꾸지 않는다.
      return withKst(clock);
    case 'BEFORE_OPEN':
      return t('financial_calendar.event.time.before_open');
    case 'AFTER_CLOSE':
      return t('financial_calendar.event.time.after_close');
    case 'DATE_ONLY':
      return t('financial_calendar.event.time.date_only');
    case 'UNKNOWN':
      return t('financial_calendar.event.time.unknown');
  }
};

/** 배지 문구. null = 배지 없음. */
export const eventReleaseBadge = (event: Pick<FinancialCalendarEvent, 'confirmed' | 'releaseStatus'>): string | null => {
  switch (parseReleaseStatus(event.releaseStatus)) {
    case null:
      return event.confirmed ? t('financial_calendar.event.confirmed_badge') : null; // 필드 없음 — 종전 로직
    case 'RELEASED':
      return t('financial_calendar.event.confirmed_badge');
    case 'SCHEDULED':
      return null; // 정본표: 배지 없음
    case 'TENTATIVE':
      return t('financial_calendar.event.release.tentative');
    case 'UNKNOWN':
      return t('financial_calendar.event.release.unknown');
  }
};

/**
 * TRAD-2917 — 날짜 표기 정본. designer 실화면 게이트: 같은 web 페이지 안에서 목록 헤더
 * ("2026년 6월 5일 (금)", Intl 경유)와 상세("2026-06-05", ISO 유사 슬라이스)가 서로 다른
 * 관례로 동시에 떠 있었다. 기준은 목록 헤더 쪽(Intl.DateTimeFormat 경유, 로케일 안전)으로
 * 확정 — `EventList.tsx`의 `DateGroupHeader`가 원래 쓰던 바로 그 로직을 그대로 옮긴다
 * (새 포맷을 짓지 않는다, `DateGroupHeader`는 이 함수를 재사용하도록 같이 바꾼다).
 *
 * 🔴 date-only 값이지만 timeZone:'UTC'를 넘기지 않는다 — `d`는 이미 로컬 자정으로 파싱돼
 * 있어(`+'T00:00:00'`, 오프셋 없음) 로컬 컴포넌트가 원래 의도한 날짜와 이미 일치한다.
 * 여기서 timeZone:'UTC'를 넘기면 UTC 뒤쪽 사용자에게 오히려 하루 밀린다.
 *
 * 🔴 이 함수는 "날짜부"만 담당한다 — 상세 화면의 `KST` 시각대 표기(TRAD-2900, 3사 중
 * web에만 있고 그게 맞는 표기)는 호출부가 별도로 이어붙인다. 여기서 시각까지 처리하면
 * 그 표기를 삼킬 위험이 있다.
 */
export const formatEventDateHeading = (dateOnly: string): string => {
  const d = new Date(dateOnly + 'T00:00:00');
  const datePart = formatLocalizedDate(d, { year: 'numeric', month: 'long', day: 'numeric' });
  const weekdayPart = formatLocalizedDate(d, { weekday: 'short' });
  return `${datePart} (${weekdayPart})`;
};

/**
 * TRAD-2720 — 상수가 아니라 함수인 이유는 `getCountryChips()`(위)와 동일: 모듈 로드
 * 시점에 `t()` 를 굳혀 두면 언어를 바꿔도 라벨이 옛 로케일에 남는다. 호출 시점에
 * 평가하도록 함수로 둔다 — 로케일이 같으면 같은 값을 내는 순수 함수다.
 * 소비자 5곳(CalendarFilterBar/CalendarFilterDrawer/EventDetailPanel/EventDetailDrawer/
 * EventCard) 전부 `getCategoryLabels()[category]` 형태로 호출 시점에 평가하도록 맞췄다.
 */
export const getCategoryLabels = (): Record<FinancialEventCategory, string> => ({
  INDICATOR: t('financial_calendar.category.indicator'),
  EARNINGS: t('financial_calendar.category.earnings'),
  DISCLOSURE: t('financial_calendar.category.disclosure'),
  CRYPTO: t('financial_calendar.category.crypto'),
});

export const CATEGORY_BADGE_CLASSES: Record<FinancialEventCategory, string> = {
  INDICATOR: 'bg-blue-100 text-blue-700',
  EARNINGS: 'bg-green-100 text-green-700',
  DISCLOSURE: 'bg-gray-100 text-gray-700',
  CRYPTO: 'bg-orange-100 text-orange-700',
};

/**
 * TRAD-3690 — 서버 이벤트의 category 로 라벨·배지를 고를 때는 아래 두 함수로. 종전 `getCategoryLabels()[e.category]` ·
 * `CATEGORY_BADGE_CLASSES[e.category]` 는 서버가 category 를 더하면 undefined(라벨 빈칸 · 배지 무색)였다.
 * 필터 칩처럼 화면이 가진 «아는 값»을 그릴 때는 종전 표를 그대로 써도 된다.
 */
export const FINANCIAL_EVENT_CATEGORY = defineServerEnum(['INDICATOR', 'EARNINGS', 'DISCLOSURE', 'CRYPTO'] as const);

export const getEventCategoryLabel = (category: string): string =>
  FINANCIAL_EVENT_CATEGORY.match(category, { ...getCategoryLabels(), UNKNOWN: unknownLabel() });

export const getEventCategoryBadgeClass = (category: string): string =>
  FINANCIAL_EVENT_CATEGORY.match(category, { ...CATEGORY_BADGE_CLASSES, UNKNOWN: 'bg-secondary text-muted-foreground' });

/** 국가 코드를 특정할 수 없을 때 쓰는 기호. `country: null` 과 미매핑 코드가 모두 여기로 모인다. */
export const GLOBAL_FLAG = '🌐';

/**
 * 국가 코드 → 표시 기호.
 *
 * `GLOBAL` 은 backend 가 **CRYPTO 이벤트에 부여하는 실제 값**이다(dev 실측 35건). 지금은
 * CRYPTO 가 화면에서 숨겨져 있어(TRAD-2141) 도달하지 않지만, 복구(TRAD-2129) 시 곧바로
 * 쓰이므로 미리 매핑해 둔다 — 없으면 화면에 `GLOBAL` 이라는 원시 문자열이 그대로 뜬다.
 */
export const COUNTRY_FLAG: Record<string, string> = {
  KR: '🇰🇷',
  US: '🇺🇸',
  EU: '🇪🇺',
  GB: '🇬🇧',
  GLOBAL: GLOBAL_FLAG,
};

/**
 * 🔴 국가 표시의 **단일 진입점** (TRAD-2309).
 *
 * 이전에는 소비처 4곳이 제각각 `COUNTRY_FLAG[c] ?? c` / `return country` 로 폴백해서,
 * **매핑에 없는 코드가 원시 문자열로 새어 나왔다** (2026-08-04 dev 실측: 리스트·상세에
 * `GB` 가 국기 자리에 평문으로 노출). 사용자 노출 문자열이 로컬라이즈 카탈로그를 거치지 않는
 * i18n 규칙 위반이자, 다른 행은 전부 국기인데 한 행만 글자라 UI 일관성도 깨진다.
 *
 * 규칙: **모르는 코드는 원시값을 보여주지 않고 `🌐` 로 떨어뜨린다.** 국가를 특정 못 하는 것과
 * `country: null` 인 것은 사용자 입장에서 같은 의미("특정 국가 아님")이므로 같은 기호로 모은다.
 *
 * 순수 함수 — 입력만으로 출력이 정해진다.
 */
export const getCountryFlag = (country: string | null | undefined): string =>
  (country && COUNTRY_FLAG[country]) || GLOBAL_FLAG;

// ─── Value trend helpers ──────────────────────────────────────────────────────

/**
 * Extracts the leading numeric portion from a value string.
 * e.g. "3.50%", "$1,234", "2.75 bps" → first parseable float.
 */
export const parseNumericValue = (val: string | null): number | null => {
  if (!val) return null;
  // Strip everything except digits, dot, minus (keep leading sign)
  const cleaned = val.replace(/[^0-9.-]/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
};

export type ValueTrend = 'up' | 'down' | 'neutral';

/** Compares actual vs previous to determine trend direction. */
export const getValueTrend = (actual: string | null, previous: string | null): ValueTrend | null => {
  const a = parseNumericValue(actual);
  const p = parseNumericValue(previous);
  if (a === null || p === null) return null;
  if (a > p) return 'up';
  if (a < p) return 'down';
  return 'neutral';
};

/*
 * TRAD-2122 (감사 §1 SoT-17) — 경제지표 숫자에는 **등락색을 쓰지 않는다.**
 *
 * `getTrendColorClass` 는 삭제됐다. TRAD-2103 이 캘린더의 서양 관례(초록=상승)를 앱 전역
 * KR 관례(price-rise/fall)로 맞췄지만, 지표 값에는 **어떤 색 규칙도 성립하지 않는다**:
 *
 *   실업률 ↑ = 악재 · GDP ↑ = 호재 · CPI ↑ = 복합
 *
 * KR 관례에서 빨강(price-rise)은 "상승·좋음"으로 읽히므로 실업률 상승을 빨강으로 칠하면
 * 호재로 오독된다. 지표마다 방향↔의미 매핑이 달라 단일 색 규칙이 존재할 수 없다.
 * 나아가 방향에 valence 를 부여하는 것은 정보 제공이 아니라 **해석·투자판단 제공**이다
 * (제품 방향: 거래 중개 아님, 투명한 정보 제공). 같은 화면 하단에 면책 문구가 붙는다.
 *
 * → 방향은 ▲▼ **기호로만** 나타내고 색은 중립(text-muted-foreground)으로 둔다.
 *   `getValueTrend` 는 그 기호를 고르기 위해 계속 쓴다.
 *
 * ⚠️ 색을 되살리지 말 것. price 계열·journal 계열 토큰을 이 값에 다시 붙이면 같은 오독이 돌아온다.
 */
